import { IconPlus } from '@developerz/ui';
import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { AccountTokenDialogs } from '../components/AccountTokenDialogs';
import { ConnectMcpPanel } from '../components/ConnectMcpPanel';
import { EmptyState } from '../components/EmptyState';
import { MintedTokenNotice } from '../components/MintedTokenNotice';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Skeleton } from '../components/Skeleton';
import { TokenRow } from '../components/TokenRow';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { createMutation } from '../lib/mutation';
import { SESSION_TOKEN } from '../lib/session';
import { createTokenRename } from '../lib/tokenRename';
import { createTokenRevoke } from '../lib/tokenRevoke';
import {
  canMintTokens,
  type MintedToken,
  type MintInput,
  mintApiToken,
  useApiTokens,
} from '../lib/tokens';

// Personal access tokens (#835) — mint / list / revoke the `dev_pat_…`
// credentials that reach `/v1` and the MCP server. Until this page existed the
// full lifecycle sat in the API with no caller, so onboarding an MCP client
// meant seeding a token by hand.
//
// Who may mint is the SERVER's answer, not a role mirror: the list carries
// `mintable_scopes`, empty exactly when the caller may not mint
// (`canMintTokens`, `lib/tokens.ts`). No vocabulary ⇒ the page reads, and says
// nothing about a button it isn't showing.

export function AccountTokens(props: { token?: string | null }): JSX.Element {
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const query = useApiTokens(token);

  const [mintOpen, setMintOpen] = createSignal(false);
  const [minted, setMinted] = createSignal<MintedToken | null>(null);

  const revoke = createTokenRevoke({ token, onRevoked: () => void query.refetch() });
  const rename = createTokenRename({ token, onRenamed: () => void query.refetch() });

  const rows = createMemo(() => query.data?.data ?? []);
  const mintable = createMemo(() => query.data?.mintable_scopes ?? []);
  const mayMint = createMemo(() => canMintTokens(query.data));

  const onMint = createMutation({
    call: (input: MintInput) => mintApiToken(input, token),
    fallback: t('account_tokens.mint_error'),
    onSuccess: (result) => {
      setMinted(result);
      setMintOpen(false);
    },
    onSettled: () => query.refetch(),
  });

  return (
    <>
      <PageHeader
        title={t('account_tokens.title')}
        desc={t('account_tokens.subtitle')}
        actions={
          <Show when={mayMint()}>
            <Button
              onClick={() => {
                onMint.clearError();
                setMintOpen(true);
              }}
            >
              <IconPlus />
              {t('account_tokens.mint')}
            </Button>
          </Show>
        }
      />

      <Show
        when={token !== null}
        fallback={<EmptyState message={t('account_tokens.no_session')} />}
      >
        <Show when={minted()}>
          {(secret) => <MintedTokenNotice minted={secret()} onDismiss={() => setMinted(null)} />}
        </Show>

        {/* Above the list, and above the mint: the OAuth connector needs no
            token, so the address is what a reader came for. The reveal-once
            notice still outranks it — that plaintext exists for one render. */}
        <ConnectMcpPanel />

        {/* QueryBoundary, not a bare Show: loading must not read as "empty". */}
        <QueryBoundary
          query={query}
          isEmpty={() => rows().length === 0}
          loading={<Skeleton height="12rem" />}
          empty={<EmptyState message={t('account_tokens.empty')} />}
        >
          {() => (
            <Panel title={t('account_tokens.panel_title')}>
              <ul class="row-list">
                <For each={rows()}>
                  {(row) => (
                    <TokenRow
                      row={row}
                      {...(row.can_manage ? { onRename: rename.ask, onRevoke: revoke.ask } : {})}
                    />
                  )}
                </For>
              </ul>
            </Panel>
          )}
        </QueryBoundary>
      </Show>

      {/* A row's rename / revoke ride that ROW's `can_manage` (#7426), never
          `mayMint()`: the server fences them by the domain's `tokenReach`, and
          a caller who may mint still manages only the rows it marks. */}
      <AccountTokenDialogs
        mint={{
          open: mintOpen(),
          mintable: mintable(),
          pending: onMint.pending(),
          error: onMint.error(),
          onMint: (input) => void onMint.run(input),
          onClose: () => setMintOpen(false),
        }}
        rename={rename}
        revoke={revoke}
      />
    </>
  );
}
